import React from 'react'
import styled from 'styled-components'
import Text from 'components/_UI/Text'
import DynamicProfileImage from 'components/commons/DynamicProfileImage'
import { getProducerProfileImage } from 'helpers/profileImage'
import paths from 'routes/paths'
import { Row } from 'styled/alignment/Row'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'

const MAX_AVATARS = 3

export function organizerHref(type, slug) {
    return type === 'establishment'
        ? `/estabelecimento/${slug}/detalhes`
        : `${paths.producer.page}/${slug}`
}

function sameName(a, b) {
    return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Conta de estabelecimento tem os dois perfis (produtor e estabelecimento), e o
 * evento dela traz os dois. Quando o estabelecimento do evento é essa mesma
 * conta, ele não é outro organizador. A lista de eventos não manda os vínculos
 * (`establishments`), então o mesmo nome também conta como repetido.
 */
export function hostIsProducer(event) {
    const producer = event?.producer_profile
    const host = event?.establishment

    if (!producer?.name || !host?.name) return false

    const linked = (producer.establishments || []).some(item => item.id === host.id)

    return linked || sameName(producer.name, host.name)
}

/**
 * Quem organiza o evento, em ordem: o produtor dono, o estabelecimento que o
 * evento já mostra como organizador (se não for o próprio produtor) e os
 * convidados (permissões ou equipes) que são produtor ou estabelecimento.
 */
export function buildOrganizers(event) {
    const owner = event?.producer_profile?.name ? [{
        key: `producer-${event.producer_profile.id}`,
        name: event.producer_profile.name,
        photo: getProducerProfileImage(event.producer_profile),
        href: organizerHref('producer', event.producer_profile.slug),
        role: 'Produtor principal',
    }] : []

    const host = event?.establishment?.name && !hostIsProducer(event) ? [{
        key: `establishment-${event.establishment.id}`,
        name: event.establishment.name,
        photo: event.establishment.profile_image_id?.thumb,
        href: organizerHref('establishment', event.establishment.slug),
        role: 'Estabelecimento do evento',
    }] : []

    const guests = (event?.co_organizers || []).map(item => ({
        key: `${item.type}-${item.id}`,
        name: item.name,
        photo: item.photo,
        href: organizerHref(item.type, item.slug),
        role: item.type === 'establishment' ? 'Estabelecimento convidado' : 'Produtor convidado',
    }))

    return [...owner, ...host, ...guests]
}

function separatorBefore(index, total) {
    if (index === 0) return null
    return index === total - 1 ? ' e ' : ', '
}

/** "Organizado por A, B e C", com as fotos sobrepostas e cada nome levando ao perfil. */
export default function CoverOrganizers({ organizers }) {
    return (
        <Container>
            <Content>
                <Avatars>
                    {organizers.slice(0, MAX_AVATARS).map((organizer, index) => (
                        <AvatarSlot key={organizer.key} first={index === 0}>
                            <DynamicProfileImage size='24px' img={organizer.photo} word={organizer.name} />
                        </AvatarSlot>
                    ))}
                </Avatars>

                <Text color={colors.nightDark} paddingBottom={'0'} marginLeft={'10px'}>
                    Organizado por&nbsp;
                    {organizers.map((organizer, index) => (
                        <React.Fragment key={organizer.key}>
                            {separatorBefore(index, organizers.length)}
                            <OrganizerLink href={organizer.href} target='_blank' rel='noopener noreferrer'>
                                {organizer.name}
                            </OrganizerLink>
                        </React.Fragment>
                    ))}
                </Text>
            </Content>
        </Container>
    )
}

const Container = styled(Row)`
    width: 100%;
`

const Content = styled(Row)`
    width: 100%;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 15px;
    text-align: left;

    @media screen and (min-width: ${breakpoints.lg}) {
        p {
            color: ${colors.neutral} !important;
        }
    }
`

const Avatars = styled(Row)`
    width: auto;
    flex-shrink: 0;
`

const AvatarSlot = styled.div`
    margin-left: ${({ first }) => first ? '0' : '-8px'};
    border-radius: 50%;
    box-shadow: 0 0 0 2px ${colors.neutralLight};
`

const OrganizerLink = styled.a`
    color: inherit;
    font-weight: 700;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`
